import { useCallback, useState } from 'react';
import { DEFAULT_REACTION_PRESETS } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';

interface ReactionBarProps {
  onSendReaction: (reactionId: string, icon?: string, label?: string) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  isRecording: boolean;
  cooldown: boolean;
  playingSoundbite: string | null;
  onOpenSoundbiteSheet: () => void;
}

const defaultPresetEntries = Object.entries(DEFAULT_REACTION_PRESETS);

/** How many reactions to show before collapsing */
const VISIBLE_COUNT = 5;

const USAGE_KEY = 'flip7_reaction_usage';

function getLocalUsage(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(USAGE_KEY) || '{}');
  } catch { return {}; }
}

function trackLocalUsage(reactionId: string) {
  const usage = getLocalUsage();
  usage[reactionId] = (usage[reactionId] ?? 0) + 1;
  localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
}

function sortByUsage(entries: [string, { icon: string; label: string }][], usage: Record<string, number>): [string, { icon: string; label: string }][] {
  return [...entries].sort((a, b) => (usage[b[0]] ?? 0) - (usage[a[0]] ?? 0));
}

export function ReactionBar({
  onSendReaction,
  onStartRecording,
  onStopRecording,
  isRecording,
  cooldown,
  playingSoundbite,
  onOpenSoundbiteSheet,
}: ReactionBarProps) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [, setUsageTick] = useState(0);

  // Use custom reactions if user has them, otherwise fall back to defaults
  const rawEntries: [string, { icon: string; label: string }][] = user?.customReactions
    ? user.customReactions.map(r => [r.id, { icon: r.icon, label: r.label }])
    : defaultPresetEntries;

  // Use DB usage for authenticated users, localStorage for guests
  const usage = user?.reactionUsage ?? getLocalUsage();
  const reactionEntries = sortByUsage(rawEntries, usage);

  const handleSendReaction = useCallback((id: string, icon?: string, label?: string) => {
    trackLocalUsage(id);
    setUsageTick(t => t + 1);
    onSendReaction(id, icon, label);
  }, [onSendReaction]);

  const hasOverflow = reactionEntries.length > VISIBLE_COUNT;
  const visibleReactions = expanded ? reactionEntries : reactionEntries.slice(0, VISIBLE_COUNT);

  return (
    <div className="reaction-bar-container">
      {/* Expanded grid — shown above the bar when toggled */}
      {expanded && hasOverflow && (
        <div className="reaction-bar__expanded-grid">
          {reactionEntries.slice(VISIBLE_COUNT).map(([id, preset]) => (
            <button
              key={id}
              className="reaction-bar__preset"
              onClick={() => handleSendReaction(id, preset.icon, preset.label)}
              disabled={cooldown}
              aria-label={preset.label}
            >
              <span className="reaction-bar__preset-icon">{preset.icon}</span>
              <span>{preset.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Emoji reactions row */}
      <div className="reaction-bar reaction-bar--no-border">
        <button
          onPointerDown={onStartRecording}
          onPointerUp={onStopRecording}
          onPointerLeave={isRecording ? onStopRecording : undefined}
          className={`reaction-bar__mic${isRecording ? ' reaction-bar__mic--recording recording-pulse' : ''}`}
          aria-label={isRecording ? 'Recording... release to send' : 'Hold to record voice memo'}
        >
          {isRecording ? '\u23F9' : '\uD83C\uDFA4'}
        </button>

        {/* Soundbite trigger — always opens sheet */}
        <button
          className={`reaction-bar__soundbite-trigger${playingSoundbite ? ' reaction-bar__soundbite-trigger--playing' : ''}`}
          onClick={onOpenSoundbiteSheet}
          aria-label={playingSoundbite ? 'Soundbites (playing)' : 'Open soundbites'}
        >
          {'\uD83D\uDD0A'}
        </button>

        <div className="reaction-bar__scroll-row">
          {visibleReactions.map(([id, preset]) => (
            <button
              key={id}
              className="reaction-bar__preset"
              onClick={() => handleSendReaction(id, preset.icon, preset.label)}
              disabled={cooldown}
              aria-label={preset.label}
            >
              <span className="reaction-bar__preset-icon">{preset.icon}</span>
              <span>{preset.label}</span>
            </button>
          ))}
        </div>

        {hasOverflow && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`reaction-bar__expand-btn${expanded ? ' reaction-bar__expand-btn--active' : ''}`}
            aria-label={expanded ? 'Show fewer reactions' : `Show ${reactionEntries.length - VISIBLE_COUNT} more reactions`}
          >
            {expanded ? '\u2212' : `+${reactionEntries.length - VISIBLE_COUNT}`}
          </button>
        )}
      </div>
    </div>
  );
}
